/**
 * Container metadata parser (EXIF / JFIF / ICC / PNG pHYs).
 *
 * Pure bytes in, plain data out: no DOM, no I/O, no decoding. The browser sniffs
 * the first 64 KB of the uploaded file anyway, and that head contains all the
 * metadata segments for every realistic photo, so this module reuses the same
 * buffer instead of reading the whole file a second time.
 *
 * Design rules:
 *  - never throw: a malformed segment yields fewer fields, not an exception;
 *  - never invent values: an absent tag stays `undefined`, and the consumer
 *    decides whether that is a warning (document requires DPI) or nothing;
 *  - metadata is a *supporting signal*. It must never be the sole reason for a
 *    failure, and it is never used to accuse a person of editing a photo.
 */

import type {
  DpiInfo,
  ExifMetadata,
  IccProfileInfo,
  ImageMetadata,
  JfifMetadata,
  ResolutionUnit,
} from '../types/metadata.js';

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string => {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const value = bytes[offset + i];
    if (value === undefined) break;
    out += String.fromCharCode(value);
  }
  return out;
};

const uint16 = (bytes: Uint8Array, offset: number, little: boolean): number =>
  little
    ? ((bytes[offset + 1] as number) << 8) | (bytes[offset] as number)
    : ((bytes[offset] as number) << 8) | (bytes[offset + 1] as number);

const uint32 = (bytes: Uint8Array, offset: number, little: boolean): number =>
  little
    ? (((bytes[offset + 3] as number) << 24) |
        ((bytes[offset + 2] as number) << 16) |
        ((bytes[offset + 1] as number) << 8) |
        (bytes[offset] as number)) >>>
        0
    : (((bytes[offset] as number) << 24) |
        ((bytes[offset + 1] as number) << 16) |
        ((bytes[offset + 2] as number) << 8) |
        (bytes[offset + 3] as number)) >>>
        0;

const stripNull = (value: string): string => value.replace(/\0.*$/s, '').trim();

/** JPEG APPn segments, in file order. */
interface JpegSegment {
  marker: number;
  /** Offset of the segment payload (after the 2-byte length). */
  start: number;
  length: number;
}

const jpegSegments = (bytes: Uint8Array): JpegSegment[] => {
  const segments: JpegSegment[] = [];
  let offset = 2;
  while (offset + 4 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = bytes[offset + 1] as number;
    // Standalone markers carry no length.
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2;
      continue;
    }
    if (marker === 0xda || marker === 0xd9) break; // scan data / end of image
    const length = ((bytes[offset + 2] as number) << 8) | (bytes[offset + 3] as number);
    if (length < 2) break;
    segments.push({ marker, start: offset + 4, length: length - 2 });
    offset += 2 + length;
  }
  return segments;
};

// ---------------------------------------------------------------------------
// TIFF / EXIF
// ---------------------------------------------------------------------------

const EXIF_TAGS = {
  make: 0x010f,
  model: 0x0110,
  orientation: 0x0112,
  xResolution: 0x011a,
  yResolution: 0x011b,
  resolutionUnit: 0x0128,
  software: 0x0131,
  dateTime: 0x0132,
  exifIfd: 0x8769,
  gpsIfd: 0x8825,
} as const;

const EXIF_IFD_TAGS = {
  dateTimeOriginal: 0x9003,
  colorSpace: 0xa001,
  pixelXDimension: 0xa002,
  pixelYDimension: 0xa003,
} as const;

const TYPE_SIZES: Record<number, number> = {
  1: 1, // BYTE
  2: 1, // ASCII
  3: 2, // SHORT
  4: 4, // LONG
  5: 8, // RATIONAL
  6: 1, // SBYTE
  7: 1, // UNDEFINED
  8: 2, // SSHORT
  9: 4, // SLONG
  10: 8, // SRATIONAL
  11: 4, // FLOAT
  12: 8, // DOUBLE
};

interface IfdEntry {
  tag: number;
  type: number;
  count: number;
  /** Offset (relative to the TIFF header) of the value when it is out of line. */
  valueOffset: number;
  /** Absolute offset of the entry's 4-byte value field, used for inline values. */
  valueField: number;
  /** True when the value fits into the 4-byte field itself. */
  inline: boolean;
}

const readIfd = (
  bytes: Uint8Array,
  tiffStart: number,
  directoryOffset: number,
  little: boolean,
): { entries: IfdEntry[]; next?: number } => {
  const base = tiffStart + directoryOffset;
  if (base + 2 > bytes.length) return { entries: [] };
  const count = uint16(bytes, base, little);
  const entries: IfdEntry[] = [];
  for (let index = 0; index < count; index += 1) {
    const entryOffset = base + 2 + index * 12;
    if (entryOffset + 12 > bytes.length) break;
    const tag = uint16(bytes, entryOffset, little);
    const type = uint16(bytes, entryOffset + 2, little);
    const valueCount = uint32(bytes, entryOffset + 4, little);
    const size = (TYPE_SIZES[type] ?? 1) * valueCount;
    entries.push({
      tag,
      type,
      count: valueCount,
      valueOffset: uint32(bytes, entryOffset + 8, little),
      valueField: entryOffset + 8,
      inline: size <= 4,
    });
  }
  const nextOffset = base + 2 + count * 12;
  const next =
    nextOffset + 4 <= bytes.length ? uint32(bytes, nextOffset, little) : undefined;
  return { entries, ...(next ? { next } : {}) };
};

const valuePosition = (entry: IfdEntry, tiffStart: number): number =>
  entry.inline ? -1 : tiffStart + entry.valueOffset;

const readAscii = (bytes: Uint8Array, tiffStart: number, entry: IfdEntry): string | undefined => {
  const length = Math.min(entry.count, 256);
  // A short ASCII value (≤4 bytes with its terminator) lives in the entry itself.
  const start = entry.inline ? entry.valueField : valuePosition(entry, tiffStart);
  if (start < 0) return undefined;
  const available = entry.inline ? Math.min(length, 4) : length;
  if (start + available > bytes.length) return undefined;
  const value = stripNull(asciiAt(bytes, start, available));
  return value.length > 0 ? value : undefined;
};

const readShortOrLong = (
  bytes: Uint8Array,
  tiffStart: number,
  entry: IfdEntry,
  little: boolean,
): number | undefined => {
  if (entry.inline) {
    // An inline SHORT/LONG is stored in the entry's own value field, not at an
    // offset from the TIFF header — reading `valueOffset` here returned garbage.
    if (entry.type === 3) return uint16(bytes, entry.valueField, little);
    if (entry.type === 4) return uint32(bytes, entry.valueField, little);
    return undefined;
  }
  const start = valuePosition(entry, tiffStart);
  if (start < 0) return undefined;
  if (entry.type === 3) return start + 2 <= bytes.length ? uint16(bytes, start, little) : undefined;
  if (entry.type === 4) return start + 4 <= bytes.length ? uint32(bytes, start, little) : undefined;
  return undefined;
};

const readRational = (
  bytes: Uint8Array,
  tiffStart: number,
  entry: IfdEntry,
  little: boolean,
): number | undefined => {
  let start: number;
  if (entry.inline) {
    // A RATIONAL cannot be inline; treat the field as a TIFF offset.
    start = tiffStart + entry.valueOffset;
  } else {
    start = valuePosition(entry, tiffStart);
  }
  if (start < 0 || start + 8 > bytes.length) return undefined;
  const numerator = uint32(bytes, start, little);
  const denominator = uint32(bytes, start + 4, little);
  if (denominator === 0) return undefined;
  const value = numerator / denominator;
  return Number.isFinite(value) ? value : undefined;
};

const resolutionUnitOf = (value: number | undefined): ResolutionUnit | undefined => {
  if (value === 2) return 'inch';
  if (value === 3) return 'cm';
  if (value === 1) return 'none';
  return undefined;
};

const parseExif = (bytes: Uint8Array, start: number): ExifMetadata | undefined => {
  const signature = asciiAt(bytes, start, 6);
  if (signature !== 'Exif\0\0') return undefined;
  const tiffStart = start + 6;
  if (tiffStart + 8 > bytes.length) return undefined;
  const byteOrder = asciiAt(bytes, tiffStart, 2);
  const little = byteOrder === 'II';
  if (!little && byteOrder !== 'MM') return undefined;
  if (uint16(bytes, tiffStart + 2, little) !== 0x002a) return undefined;
  const ifd0Offset = uint32(bytes, tiffStart + 4, little);

  const exif: ExifMetadata = {};
  const ifd0 = readIfd(bytes, tiffStart, ifd0Offset, little);
  let exifIfdOffset: number | undefined;
  let gpsPresent = false;

  for (const entry of ifd0.entries) {
    switch (entry.tag) {
      case EXIF_TAGS.make:
        exif.make = readAscii(bytes, tiffStart, entry);
        break;
      case EXIF_TAGS.model:
        exif.model = readAscii(bytes, tiffStart, entry);
        break;
      case EXIF_TAGS.software:
        exif.software = readAscii(bytes, tiffStart, entry);
        break;
      case EXIF_TAGS.orientation: {
        const value = readShortOrLong(bytes, tiffStart, entry, little);
        if (value !== undefined) exif.orientation = value;
        break;
      }
      case EXIF_TAGS.xResolution: {
        const value = readRational(bytes, tiffStart, entry, little);
        if (value !== undefined) exif.xResolution = value;
        break;
      }
      case EXIF_TAGS.yResolution: {
        const value = readRational(bytes, tiffStart, entry, little);
        if (value !== undefined) exif.yResolution = value;
        break;
      }
      case EXIF_TAGS.resolutionUnit: {
        const unit = resolutionUnitOf(readShortOrLong(bytes, tiffStart, entry, little));
        if (unit) exif.resolutionUnit = unit;
        break;
      }
      case EXIF_TAGS.dateTime:
        exif.dateTimeOriginal ??= readAscii(bytes, tiffStart, entry);
        break;
      case EXIF_TAGS.exifIfd:
        exifIfdOffset = readShortOrLong(bytes, tiffStart, entry, little);
        break;
      case EXIF_TAGS.gpsIfd:
        gpsPresent = true;
        break;
      default:
        break;
    }
  }

  if (exifIfdOffset !== undefined) {
    const exifIfd = readIfd(bytes, tiffStart, exifIfdOffset, little);
    for (const entry of exifIfd.entries) {
      switch (entry.tag) {
        case EXIF_IFD_TAGS.dateTimeOriginal: {
          const value = readAscii(bytes, tiffStart, entry);
          if (value) exif.dateTimeOriginal = value;
          break;
        }
        case EXIF_IFD_TAGS.colorSpace: {
          const value = readShortOrLong(bytes, tiffStart, entry, little);
          if (value !== undefined) exif.colorSpace = value;
          break;
        }
        case EXIF_IFD_TAGS.pixelXDimension: {
          const value = readShortOrLong(bytes, tiffStart, entry, little);
          if (value !== undefined) exif.pixelXDimension = value;
          break;
        }
        case EXIF_IFD_TAGS.pixelYDimension: {
          const value = readShortOrLong(bytes, tiffStart, entry, little);
          if (value !== undefined) exif.pixelYDimension = value;
          break;
        }
        default:
          break;
      }
    }
  }

  if (gpsPresent) exif.hasGps = true;
  return exif;
};

// ---------------------------------------------------------------------------
// JFIF
// ---------------------------------------------------------------------------

const parseJfif = (bytes: Uint8Array, start: number, length: number): JfifMetadata | undefined => {
  if (length < 12) return undefined;
  if (asciiAt(bytes, start, 5) !== 'JFIF\0') return undefined;
  const major = bytes[start + 5] as number;
  const minor = bytes[start + 6] as number;
  const densityUnits = bytes[start + 7] as number;
  const xDensity = uint16(bytes, start + 8, false);
  const yDensity = uint16(bytes, start + 10, false);
  const thumbWidth = bytes[start + 12] as number | undefined;
  const thumbHeight = bytes[start + 13] as number | undefined;
  return {
    version: `${major}.${String(minor).padStart(2, '0')}`,
    densityUnits,
    xDensity,
    yDensity,
    hasThumbnail: (thumbWidth ?? 0) > 0 && (thumbHeight ?? 0) > 0,
  };
};

// ---------------------------------------------------------------------------
// ICC profile
// ---------------------------------------------------------------------------

/** ICC tag signatures we care about. */
const ICC_DESC = 'desc';
const ICC_MLUC = 'mluc';

const parseIccChunk = (
  bytes: Uint8Array,
  dataStart: number,
  dataLength: number,
  state: { chunks: number; total: number; bytes: number; header?: Uint8Array },
): void => {
  if (dataLength < 14) return;
  if (asciiAt(bytes, dataStart, 12) !== 'ICC_PROFILE\0') return;
  const index = bytes[dataStart + 12] as number;
  const total = bytes[dataStart + 13] as number;
  state.chunks += 1;
  state.total = Math.max(state.total, total);
  state.bytes += dataLength - 14;
  if (index === 1) {
    const profileStart = dataStart + 14;
    const profileLength = Math.min(dataLength - 14, 4096);
    state.header = bytes.subarray(profileStart, profileStart + profileLength);
  }
};

const iccDescription = (profile: Uint8Array): string | undefined => {
  if (profile.length < 132) return undefined;
  if (asciiAt(profile, 36, 4) !== 'acsp') return undefined;
  const tagCount = uint32(profile, 128, false);
  if (tagCount === 0 || tagCount > 256) return undefined;
  for (let index = 0; index < tagCount; index += 1) {
    const entry = 132 + index * 12;
    if (entry + 12 > profile.length) break;
    const signature = asciiAt(profile, entry, 4);
    if (signature !== ICC_DESC) continue;
    const offset = uint32(profile, entry + 4, false);
    const size = uint32(profile, entry + 8, false);
    if (offset + size > profile.length || size < 12) return undefined;
    const type = asciiAt(profile, offset, 4);
    if (type === ICC_DESC) {
      // textDescriptionType (ICC v2): 4-byte ASCII count, then the string.
      const asciiCount = uint32(profile, offset + 8, false);
      const length = Math.max(0, Math.min(asciiCount, size - 12, 128));
      return stripNull(asciiAt(profile, offset + 12, length)) || undefined;
    }
    if (type === ICC_MLUC) {
      // multiLocalizedUnicodeType (ICC v4): first record wins.
      const recordCount = uint32(profile, offset + 8, false);
      const recordSize = uint32(profile, offset + 12, false);
      if (recordCount === 0 || recordSize < 12) return undefined;
      const record = offset + 16;
      if (record + 12 > profile.length) return undefined;
      const length = uint32(profile, record + 4, false);
      const stringOffset = uint32(profile, record + 8, false);
      const start = offset + stringOffset;
      if (stringOffset === 0 || start + length > profile.length) return undefined;
      let out = '';
      for (let i = 0; i + 1 < length; i += 2) {
        const code = (profile[start + i] as number) << 8 | (profile[start + i + 1] as number);
        if (code === 0) break;
        out += String.fromCharCode(code);
      }
      return out.trim() || undefined;
    }
    return undefined;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// PNG / WebP
// ---------------------------------------------------------------------------

const parsePng = (bytes: Uint8Array): { dpi?: DpiInfo; icc?: IccProfileInfo; notes: string[] } => {
  const notes: string[] = [];
  let dpi: DpiInfo | undefined;
  let icc: IccProfileInfo | undefined;
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = uint32(bytes, offset, false);
    const type = asciiAt(bytes, offset + 4, 4);
    const dataStart = offset + 8;
    if (dataStart + length > bytes.length) break;
    if (type === 'pHYs' && length >= 9) {
      const x = uint32(bytes, dataStart, false);
      const y = uint32(bytes, dataStart + 4, false);
      const unit = bytes[dataStart + 8] as number;
      if (unit === 1 && x > 0 && y > 0) {
        const xDpi = Math.round(x * 0.0254);
        const yDpi = Math.round(y * 0.0254);
        dpi = { x: xDpi, y: yDpi, source: 'png-phys', anisotropic: Math.abs(xDpi - yDpi) > 1 };
        notes.push('dpi-from-png-phys');
      }
    }
    if (type === 'iCCP' && length > 2) {
      icc = { present: true, description: stripNull(asciiAt(bytes, dataStart, Math.min(length, 79))) || undefined };
      notes.push('icc-present');
    }
    if (type === 'IEND') break;
    offset = dataStart + length + 4;
  }
  if (!icc) notes.push('icc-missing');
  return { ...(dpi ? { dpi } : {}), ...(icc ? { icc } : {}), notes };
};

const parseWebp = (bytes: Uint8Array): { icc?: IccProfileInfo; notes: string[] } => {
  const notes: string[] = [];
  let icc: IccProfileInfo | undefined;
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const type = asciiAt(bytes, offset, 4);
    const length = uint32(bytes, offset + 4, true);
    if (type === 'ICCP') {
      icc = { present: true, bytes: length };
      notes.push('icc-present');
      break;
    }
    offset += 8 + length + (length % 2);
  }
  if (!icc) notes.push('icc-missing');
  return { ...(icc ? { icc } : {}), notes };
};

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export const parseImageMetadata = (bytes: Uint8Array, format: string): ImageMetadata => {
  const notes: string[] = [];
  const metadata: ImageMetadata = { parsedBytes: bytes.length, notes };

  if (format === 'png') {
    const png = parsePng(bytes);
    notes.push(...png.notes);
    if (png.icc) metadata.exif = { iccProfile: png.icc };
    if (png.dpi) metadata.dpi = png.dpi;
    return metadata;
  }

  if (format === 'webp') {
    const webp = parseWebp(bytes);
    notes.push(...webp.notes);
    if (webp.icc) metadata.exif = { iccProfile: webp.icc };
    return metadata;
  }

  if (format !== 'jpeg') {
    notes.push('metadata-format-unparsed');
    return metadata;
  }

  const segments = jpegSegments(bytes);
  const iccState: { chunks: number; total: number; bytes: number; header?: Uint8Array } = {
    chunks: 0,
    total: 0,
    bytes: 0,
  };
  let exif: ExifMetadata | undefined;
  let jfif: JfifMetadata | undefined;

  for (const segment of segments) {
    if (segment.marker === 0xe1) {
      exif ??= parseExif(bytes, segment.start);
    } else if (segment.marker === 0xe0) {
      jfif ??= parseJfif(bytes, segment.start, segment.length);
    } else if (segment.marker === 0xe2) {
      parseIccChunk(bytes, segment.start, segment.length, iccState);
    }
  }

  if (exif) {
    notes.push('exif-present');
    if (iccState.chunks > 0 && iccState.header) {
      const profile = iccState.header;
      exif.iccProfile = {
        present: true,
        bytes: iccState.bytes,
        ...(iccDescription(profile) ? { description: iccDescription(profile) as string } : {}),
        ...(asciiAt(profile, 16, 4).trim() ? { colourSpace: asciiAt(profile, 16, 4) } : {}),
      };
      notes.push('icc-present');
    }
  } else if (iccState.chunks > 0) {
    // ICC without EXIF: still worth reporting for the colour-profile check.
    exif = {
      iccProfile: {
        present: true,
        bytes: iccState.bytes,
        ...(iccState.header && iccDescription(iccState.header)
          ? { description: iccDescription(iccState.header) as string }
          : {}),
      },
    };
    notes.push('icc-present');
  }
  if (!exif) notes.push('no-exif');
  if (iccState.chunks === 0) notes.push('icc-missing');
  if (jfif) notes.push('jfif-present');
  if (exif && exif.orientation !== undefined && exif.orientation !== 1) {
    notes.push(`orientation-${exif.orientation}`);
  }

  if (exif) metadata.exif = exif;
  if (jfif) metadata.jfif = jfif;

  const dpi = resolveDpi(exif, jfif);
  if (dpi) metadata.dpi = dpi;
  return metadata;
};

/**
 * Effective DPI from the metadata that is actually present.
 *
 * EXIF resolution wins over JFIF because it is the field photo tools write;
 * JFIF `densityUnits: 0` means "aspect ratio only" and yields nothing at all —
 * guessing 72 DPI there would be inventing data.
 */
export const resolveDpi = (
  exif: ExifMetadata | undefined,
  jfif: JfifMetadata | undefined,
): DpiInfo | undefined => {
  const unitFactor = (unit: ResolutionUnit | undefined): number =>
    unit === 'cm' ? 2.54 : 1;

  if (exif?.xResolution && exif.yResolution && (exif.resolutionUnit ?? 'inch') !== 'none') {
    const factor = unitFactor(exif.resolutionUnit ?? 'inch');
    const x = Math.round(exif.xResolution * factor);
    const y = Math.round(exif.yResolution * factor);
    if (x > 0 && y > 0) {
      return { x, y, source: 'exif', anisotropic: Math.abs(x - y) > 1 };
    }
  }

  if (jfif && jfif.densityUnits !== 0 && jfif.xDensity && jfif.yDensity) {
    const factor = jfif.densityUnits === 2 ? 2.54 : 1;
    const x = Math.round(jfif.xDensity * factor);
    const y = Math.round(jfif.yDensity * factor);
    if (x > 0 && y > 0) {
      return { x, y, source: 'jfif', anisotropic: Math.abs(x - y) > 1 };
    }
  }

  return undefined;
};
